import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const KeyTermsForm = ({ formData, onChange, errors }) => {
  const currencies = [
    { value: 'USD', label: 'US Dollar (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' },
    { value: 'GBP', label: 'British Pound (GBP)' },
    { value: 'CAD', label: 'Canadian Dollar (CAD)' },
    { value: 'AUD', label: 'Australian Dollar (AUD)' }
  ];

  const renewalTerms = [
    { value: 'none', label: 'No Auto-Renewal' },
    { value: '1-month', label: '1 Month' },
    { value: '3-months', label: '3 Months' },
    { value: '6-months', label: '6 Months' },
    { value: '1-year', label: '1 Year' },
    { value: '2-years', label: '2 Years' }
  ];

  const paymentTerms = [
    { value: 'net-15', label: 'Net 15 Days' },
    { value: 'net-30', label: 'Net 30 Days' },
    { value: 'net-45', label: 'Net 45 Days' },
    { value: 'net-60', label: 'Net 60 Days' },
    { value: 'due-on-receipt', label: 'Due on Receipt' },
    { value: 'advance-payment', label: 'Advance Payment' }
  ];

  const handleInputChange = (field, value) => {
    onChange(field, value);
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    const numericValue = value?.replace(/[^\d.]/g, '');
    const number = parseFloat(numericValue);
    if (isNaN(number)) return '';
    return number?.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleCurrencyChange = (field, value) => {
    const formatted = formatCurrency(value);
    handleInputChange(field, formatted);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
          <span className="text-primary font-semibold text-sm">3</span>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Key Terms & Conditions</h3>
          <p className="text-sm text-muted-foreground">Define contract duration, value, and key terms</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Contract Duration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Start Date"
            type="date"
            value={formData?.startDate || ''}
            onChange={(e) => handleInputChange('startDate', e?.target?.value)}
            error={errors?.startDate}
            required
            description="When the contract becomes effective"
          />

          <Input
            label="End Date"
            type="date"
            value={formData?.endDate || ''}
            onChange={(e) => handleInputChange('endDate', e?.target?.value)}
            error={errors?.endDate}
            description="When the contract expires (leave blank for indefinite)"
          />
        </div>

        {/* Contract Value */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Select
            label="Currency"
            options={currencies}
            value={formData?.currency || 'USD'}
            onChange={(value) => handleInputChange('currency', value)}
            required
          />

          <Input
            label="Contract Value"
            type="text"
            placeholder="0.00"
            value={formData?.contractValue || ''}
            onChange={(e) => handleCurrencyChange('contractValue', e?.target?.value)}
            error={errors?.contractValue}
            description="Total contract value"
          />

          <Select
            label="Payment Terms"
            options={paymentTerms}
            value={formData?.paymentTerms || 'net-30'}
            onChange={(value) => handleInputChange('paymentTerms', value)}
            required
          />
        </div>

        {/* Auto-Renewal Settings */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Icon name="RotateCcw" size={20} className="text-accent" />
            <h4 className="font-medium text-foreground">Auto-Renewal Settings</h4>
          </div>

          <Checkbox
            label="Enable Auto-Renewal"
            description="Automatically renew this contract when it expires"
            checked={formData?.autoRenewal || false}
            onChange={(e) => handleInputChange('autoRenewal', e?.target?.checked)}
          />

          {formData?.autoRenewal && (
            <div className="ml-6 space-y-4">
              <Select
                label="Renewal Term"
                options={renewalTerms}
                value={formData?.renewalTerm || '1-year'}
                onChange={(value) => handleInputChange('renewalTerm', value)}
                description="How long each renewal period lasts"
              />

              <Input
                label="Notice Period (Days)"
                type="number"
                placeholder="30"
                value={formData?.renewalNoticePeriod || ''}
                onChange={(e) => handleInputChange('renewalNoticePeriod', e?.target?.value)}
                description="Days before expiration to send renewal notice"
                min="1"
                max="365"
              />
            </div>
          )}
        </div>

        {/* Termination Clause */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <h4 className="font-medium text-foreground">Termination Terms</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Termination Notice Period (Days)"
              type="number"
              placeholder="30"
              value={formData?.terminationNotice || ''}
              onChange={(e) => handleInputChange('terminationNotice', e?.target?.value)}
              description="Required notice period for termination"
              min="0"
              max="365"
            />

            <Checkbox
              label="Allow Termination for Convenience"
              description="Either party can terminate without cause"
              checked={formData?.terminationForConvenience || false}
              onChange={(e) => handleInputChange('terminationForConvenience', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Governing Law */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Icon name="Scale" size={20} className="text-primary" />
            <h4 className="font-medium text-foreground">Legal Framework</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Governing Law"
              type="text"
              placeholder="e.g., State of New York, United States"
              value={formData?.governingLaw || ''}
              onChange={(e) => handleInputChange('governingLaw', e?.target?.value)}
              description="Which jurisdiction's laws govern this contract"
            />

            <Input
              label="Dispute Resolution"
              type="text"
              placeholder="e.g., Arbitration, Mediation, Court"
              value={formData?.disputeResolution || ''}
              onChange={(e) => handleInputChange('disputeResolution', e?.target?.value)}
              description="How disputes will be resolved"
            />
          </div>
        </div>

        {/* Additional Terms */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={20} className="text-muted-foreground" />
            <h4 className="font-medium text-foreground">Additional Terms</h4>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Special Conditions</label>
            <textarea
              placeholder="Enter any special conditions, clauses, or terms specific to this contract..."
              value={formData?.specialConditions || ''}
              onChange={(e) => handleInputChange('specialConditions', e?.target?.value)}
              className="w-full min-h-[100px] px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
            />
            <p className="text-xs text-muted-foreground">
              Include any special terms, conditions, or clauses that are unique to this contract.
            </p>
          </div>
        </div>

        {/* Confidentiality */}
        <div className="space-y-4">
          <Checkbox
            label="Include Confidentiality Clause"
            description="Add standard confidentiality and non-disclosure terms"
            checked={formData?.includeConfidentiality || false}
            onChange={(e) => handleInputChange('includeConfidentiality', e?.target?.checked)}
          />

          <Checkbox
            label="Include Intellectual Property Clause"
            description="Add standard intellectual property ownership terms"
            checked={formData?.includeIntellectualProperty || false}
            onChange={(e) => handleInputChange('includeIntellectualProperty', e?.target?.checked)}
          />

          <Checkbox
            label="Include Force Majeure Clause"
            description="Add protection against unforeseeable circumstances"
            checked={formData?.includeForceMajeure || false}
            onChange={(e) => handleInputChange('includeForceMajeure', e?.target?.checked)}
          />
        </div>
      </div>
    </div>
  );
};

export default KeyTermsForm;